import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import attendanceAPI from '../api/attendanceAPI';
import { useAttendanceAuth } from '../context/AttendanceAuthContext';
import StudentList from './StudentList';
import { 
  MdArrowBack, 
  MdSchedule,
  MdPeople,
  MdSubject,
  MdCheckCircle,
  MdCancel,
  MdEdit
} from 'react-icons/md';
import { normalizeSession, getBatchesArray } from '../utils/sessionUtils';

import { formatDateReadable } from '../utils/dateUtils';

/**
 * Faculty Session Details Component ok man
 * Read-only view of session attendance for faculty admin
 */
function FacultySessionDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const facultySessionsPath = `/attendance/faculty-admin/sessions${location.search || ''}`;
  const { getToken } = useAttendanceAuth();
  const [session, setSession] = useState(null);
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchSessionData();
  }, [id]);

  const fetchSessionData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = getToken();
      const result = await attendanceAPI.getFacultySession(id, token);
      
      console.log('FacultySessionDetails - fetch result:', result);
      
      if (result && (result.success || result.session)) {
        // Handle both success: true format and direct session data format
        const sessionData = result.session || result;
        setSession(normalizeSession(sessionData));
        setStudents(result.students || []);
        setStats(result.summary || result.statistics || null);
        
        // Map attendance data
        const attendanceMap = {};
        (result.students || []).forEach(student => {
          const studentId = student.student_id || student.id;
          attendanceMap[studentId] = student.is_present ? 'present' : 'absent';
        });
        setAttendance(attendanceMap);
      } else {
        const errorMsg = result?.message || result?.error?.message || 'Failed to load session data';
        // Check if it's an "Odoo Server Error" - might be a non-critical error
        if (errorMsg.toLowerCase().includes('odoo server error')) {
          console.warn('⚠️ FacultySessionDetails: Got "Odoo Server Error" but session might still be accessible');
          // Try to use whatever data we got, even if result.success is false
          if (result && result.session) {
            setSession(normalizeSession(result.session));
            setStudents(result.students || []);
            setStats(result.summary || result.statistics || null);
            const attendanceMap = {};
            (result.students || []).forEach(student => {
              const studentId = student.student_id || student.id;
              attendanceMap[studentId] = student.is_present ? 'present' : 'absent';
            });
            setAttendance(attendanceMap);
            return; // Don't set error if we got usable data
          }
        }
        setError(errorMsg);
      }
    } catch (err) {
      console.error('Error fetching session data:', err);
      const errorMessage = err.message || 'Failed to load session data';
      
      // Check if it's an "Odoo Server Error"
      if (errorMessage.toLowerCase().includes('odoo server error')) {
        console.warn('⚠️ FacultySessionDetails: Caught "Odoo Server Error"');
        // For now, show a more user-friendly message
        setError('Unable to load session details. The session may have been saved successfully. Please refresh the page.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading session details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center space-x-2 mb-2">
          <h3 className="text-red-800 font-semibold">Error</h3>
        </div>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={() => navigate(facultySessionsPath)}
          className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition"
        >
          Back to Sessions
        </button>
      </div>
    );
  }

  if (!session) {
    return null;
  }

  const batches = getBatchesArray(session.batches);
  const presentCount = stats?.present || Object.values(attendance).filter(s => s === 'present').length;
  const absentCount = stats?.absent || Object.values(attendance).filter(s => s === 'absent').length;
  const totalCount = stats?.total || students.length;
  const attendanceRate = totalCount > 0 ? ((presentCount / totalCount) * 100).toFixed(1) : 0;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="border-b-2 border-gray-300 pb-4 sm:pb-6">
        <button
          onClick={() => navigate(facultySessionsPath)}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 mb-3 sm:mb-4 transition-colors text-xs sm:text-sm md:text-base"
        >
          <MdArrowBack className="text-lg sm:text-xl" />
          <span>Back to Sessions</span>
        </button>

        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800">Session Details</h1>
      </div>

      {/* Session Info Card */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="flex items-start space-x-3 p-3 bg-white rounded-lg border border-blue-100">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <MdSubject className="text-blue-600 text-xl" />
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Subject</p>
                <p className="font-bold text-gray-900 text-sm sm:text-base truncate">{session.subject || 'N/A'}</p>
              </div>
            </div>

            {batches.length > 0 && (
              <div className="flex items-start space-x-3 p-3 bg-white rounded-lg border border-blue-100">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <MdPeople className="text-blue-600 text-xl" />
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Batch(es)</p>
                  <p className="font-bold text-gray-900 text-sm sm:text-base">{batches.join(', ')}</p>
                </div>
              </div>
            )}

            <div className="flex items-start space-x-3 p-3 bg-white rounded-lg border border-blue-100">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <MdSchedule className="text-blue-600 text-xl" />
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Date & Time</p>
                <p className="font-bold text-gray-900 text-sm sm:text-base">
                  {session.session_date ? formatDateReadable(session.session_date) : 'N/A'}
                  {session.time_start && session.time_end && (
                    <span className="block text-xs font-normal text-gray-600 mt-1">
                      {session.time_start} - {session.time_end}
                    </span>
                  )}
                </p>
              </div>
            </div>

            {session.period && (
              <div className="flex items-center space-x-3">
                <MdSchedule className="text-blue-500 text-xl" />
                <div>
                  <p className="text-sm text-gray-500">Period</p>
                  <p className="font-semibold text-gray-800">{session.period}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mb-4 sm:mb-6 flex justify-end gap-2 sm:gap-3 w-full">
          {/* Mark Attendance button - shown for unmarked sessions */}
          {session.state?.toLowerCase() !== 'marked' ? (
            <button
              onClick={() => navigate(`/attendance/faculty-admin/mark/${id}${location.search || ''}`)}
              className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all font-semibold flex items-center justify-center space-x-2 text-sm sm:text-base shadow-md hover:shadow-lg"
            >
              <MdSchedule />
              <span>Mark Attendance</span>
            </button>
          ) : (
            <div className="flex w-full gap-2 sm:gap-3">
              {/* Already Marked button - left side (disabled) */}
              <button
                disabled
                className="flex-1 px-4 sm:px-6 py-2.5 sm:py-3 bg-gray-400 text-white rounded-lg cursor-not-allowed flex items-center justify-center space-x-2 text-sm sm:text-base font-semibold shadow-md opacity-75"
                title="Attendance already marked"
              >
                <MdCheckCircle />
                <span>Attendance Already Marked</span>
              </button>
              
              {/* Edit Attendance button - right side */}
              <button
                onClick={() => navigate(`/attendance/faculty-admin/mark/${id}${location.search || ''}`)}
                className="flex-1 px-4 sm:px-6 py-2.5 sm:py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all font-semibold flex items-center justify-center space-x-2 text-sm sm:text-base shadow-md hover:shadow-lg"
                title="Edit marked attendance"
              >
                <MdEdit />
                <span>Edit Attendance</span>
              </button>
            </div>
          )}
        </div>

        {/* Statistics Card */}
        <div className="bg-gradient-to-r from-blue-50 to-green-50 border-2 border-blue-300 rounded-xl shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
          <h2 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4">Attendance Summary</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            <div className="text-center p-2 sm:p-3 bg-white rounded-lg border border-gray-200">
              <p className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800">{totalCount}</p>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">Total Students</p>
            </div>
            <div className="text-center p-2 sm:p-3 bg-white rounded-lg border border-gray-200">
              <div className="flex items-center justify-center space-x-1">
                <MdCheckCircle className="text-green-500 text-lg sm:text-xl" />
                <p className="text-xl sm:text-2xl md:text-3xl font-bold text-green-600">{presentCount}</p>
              </div>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">Present</p>
            </div>
            <div className="text-center p-2 sm:p-3 bg-white rounded-lg border border-gray-200">
              <div className="flex items-center justify-center space-x-1">
                <MdCancel className="text-red-500 text-lg sm:text-xl" />
                <p className="text-xl sm:text-2xl md:text-3xl font-bold text-red-600">{absentCount}</p>
              </div>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">Absent</p>
            </div>
            <div className="text-center p-2 sm:p-3 bg-white rounded-lg border border-gray-200">
              <p className="text-xl sm:text-2xl md:text-3xl font-bold text-blue-600">{attendanceRate}%</p>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">Attendance Rate</p>
            </div>
          </div>
        </div>

      {/* Student List */}
      <div className="bg-white border-2 border-gray-300 rounded-xl shadow-sm p-4 sm:p-6">
        <StudentList
          students={students}
          attendance={attendance}
          onAttendanceChange={() => {}} // No-op for read-only
          readOnly={true}
          showSearch={true}
        />
      </div>
    </div>
  );
}

export default FacultySessionDetails;

